import * as React from 'react';
import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { Icon } from '@rbilabs/components-library/build/components/icon';

import { IncentiveEvaluationMessages } from 'components/incentive-evaluation-messages/incentive-evaluation-messages';
import Picture from 'components/picture';
import usePosVendor from 'hooks/menu/use-pos-vendor';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { useMenuContext } from 'state/menu';
import { useStoreContext } from 'state/store';
import { isMobile } from 'utils/environment';
import { scrollContainerToChild } from 'utils/scroll';

import { LoyaltyIncentivesGrid } from '../loyalty-incentives-components';
import { IncentiveCard } from '../loyalty-incentives-components/incentive-card/incentive-card';
import { REWARD_CARD_HEIGHT } from '../loyalty-incentives-components/incentive-card/incentive-card.styled';
import { parseIncentiveData } from '../loyalty-incentives-components/incentive-card/parse-incentive-data';
import { RewardCardContent } from '../loyalty-incentives-components/incentive-card/reward-card-content';

import {
  AccordionTrigger,
  CATEGORY_TRANSITION_TIME,
  CardWrapper,
  CarouselHeading,
  CarouselHeadingContainer,
  CarouselHeadingLabel,
  CarouselItem,
  CarouselItemPoints,
  CarouselRewardLock,
  CarouselRewardName,
  CarouselSection,
  CarouselSectionRewards,
  CategoryHeader,
  CategoryImage,
  CategoryLabel,
  DelayedUnmountRewardContainer,
  HeadingLockedIcon,
  HeadingLoyaltyIcon,
  ItemLoyaltyIcon,
  LoyaltyRewardsTitle,
  PictureWrapper,
  PointsNeededText,
  RewardCategoryAccordion,
  StyledRewardsCarousel,
} from './loyalty-rewards.styled';
import { ILoyaltyRewardsViewProps } from './types';

export const LoyaltyRewardsViewBase: FC<ILoyaltyRewardsViewProps> = ({
  handleClick,
  sanityRewardsMap,
  selectedCategories,
  setSelectedCategories,
  engineRewardsMap,
  formatMessage,
  selectedReward,
  rewardsTabPath,
  rewardCategories,
  refetchRewards,
  rewardsEvaluationMap,
  title,
  isWidget,
  isCarousel,
  setSelectedRewardIdFromWidget,
}) => {
  // Prefetch rewards availability
  const enableIapPrefetching = useFlag(LaunchDarklyFlag.ENABLE_IAP_PREFETCHING);
  const { checkItemAvailability } = useMenuContext();
  const { store } = useStoreContext();
  const { vendor } = usePosVendor();

  const rewardsRoute = `${formatMessage({ id: 'routes.loyaltyHome' })}/${rewardsTabPath}`;
  const [lastSelectedCategory, setLastSelectedCategory] = useState('');
  const lastSelectedCategoryRef = useRef<HTMLLIElement | null>();
  const [scrollTimeoutId, setScrollTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const lockOnImageEnabled = Boolean(useFlag(LaunchDarklyFlag.ENABLE_LOCK_ON_REWARD_IMAGE));
  const { loyaltyUser } = useLoyaltyUser();
  const userPoints = loyaltyUser?.points || 0;

  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const setScrollContainerRef = (e: HTMLElement | null) => {
    scrollContainerRef.current = e;
  };

  const carouselCardMapper = (reward: any) => {
    const rewardId = reward?._id;
    const incentive = sanityRewardsMap[rewardId || ''];

    const loyaltyRewardId = incentive?.loyaltyEngineId || '';
    const engineReward = engineRewardsMap[loyaltyRewardId];
    const rewardLocked = userPoints < engineReward.pointCost;

    if (!incentive) {
      return null;
    }

    const incentiveData = parseIncentiveData(incentive);
    const incentiveSanityId = incentiveData.id;
    const incentiveName = incentiveData.name;
    const loyaltyEngineId = incentiveData.loyaltyEngineId;

    return (
      <CarouselItem
        key={rewardId}
        onClick={() => {
          if (rewardLocked) {
            return;
          }
          handleClick({ incentiveName, loyaltyEngineId, incentiveSanityId })();
        }}
      >
        <CardWrapper locked={rewardLocked}>
          <PictureWrapper>
            <Picture
              image={incentiveData.image}
              alt={incentiveData.imageDescription}
              objectFitContain={true}
            />
          </PictureWrapper>

          <CarouselRewardName>{incentiveData.name}</CarouselRewardName>

          <CarouselItemPoints locked={rewardLocked}>
            {rewardLocked ? <CarouselRewardLock /> : <ItemLoyaltyIcon />}
            {engineReward.pointCost}
          </CarouselItemPoints>
        </CardWrapper>
      </CarouselItem>
    );
  };

  useEffect(() => {
    // When used as widget in the reward dashboard, we don't want to add/remove bottom margin
    if (isWidget) {
      return;
    }
    // A timeout with `CATEGORY_TRANSITION_TIME` is needed because after the transition ends,
    // the height of the window changes, affecting the scroll position
    const currentScrollTimeoutId = global.setTimeout(() => {
      if (lastSelectedCategoryRef.current && scrollContainerRef.current) {
        scrollContainerToChild(scrollContainerRef.current, lastSelectedCategoryRef.current);
      }

      setScrollTimeoutId(null);
    }, CATEGORY_TRANSITION_TIME);

    setScrollTimeoutId(currentScrollTimeoutId);

    return () => clearTimeout(currentScrollTimeoutId);
  }, [isWidget, lastSelectedCategory, setScrollTimeoutId]);

  // Create array of valid incentive item ids from reward categories
  const validItemIds = useMemo(
    () =>
      enableIapPrefetching
        ? rewardCategories.reduce<string[]>((categoriesAcc, { label, rewards }) => {
            const categoryLabel = label?.en;
            if (rewards?.length && categoryLabel) {
              const categoryPoints = rewards[0]?.loyaltyEngineId
                ? engineRewardsMap[rewards[0]?.loyaltyEngineId].pointCost
                : parseInt(categoryLabel, 10);

              if (userPoints >= categoryPoints) {
                return rewards.reduce<string[]>((rewardsAcc, reward) => {
                  if (reward?._id) {
                    const firstIncentive = sanityRewardsMap[reward._id].incentives?.[0];
                    if (firstIncentive) {
                      const { _type, _id } = firstIncentive;
                      const itemId = `${_type}-${_id}`;
                      if (!rewardsAcc.includes(itemId)) {
                        rewardsAcc.push(`${_type}-${_id}`);
                      }
                    }
                  }
                  return rewardsAcc;
                }, categoriesAcc);
              }
            }
            return categoriesAcc;
          }, [])
        : [],
    [enableIapPrefetching, engineRewardsMap, rewardCategories, sanityRewardsMap, userPoints]
  );

  // Iterate through valid reward items ids to prefetch availability
  useEffect(() => {
    if (enableIapPrefetching) {
      validItemIds.forEach(itemId => {
        checkItemAvailability({
          vendor,
          restaurantPosDataId: store.restaurantPosData?._id ?? '',
          storeNumber: store.number ?? '',
          itemId,
        });
      });
    }
  }, [enableIapPrefetching, validItemIds, checkItemAvailability, store, vendor]);

  if (isCarousel) {
    return (
      <LoyaltyIncentivesGrid
        selectedIncentive={selectedReward}
        refetch={refetchRewards}
        goBack={rewardsRoute}
        engineIncentivesMap={engineRewardsMap}
        setScrollContainerRef={setScrollContainerRef}
        offersInCooldown={false}
        isWidget={isWidget}
        isCarousel={isCarousel}
        setSelectedRewardIdFromWidget={setSelectedRewardIdFromWidget}
      >
        {title && <LoyaltyRewardsTitle>{title}</LoyaltyRewardsTitle>}
        {rewardCategories.map(category => {
          const categoryLabel = category.label?.en;
          if (!category.rewards?.length || !categoryLabel) {
            return null;
          }

          const categoryPoints = category.rewards[0]?.loyaltyEngineId
            ? engineRewardsMap[category.rewards[0]?.loyaltyEngineId].pointCost
            : parseInt(categoryLabel, 10);

          const formattedCategoryLabelId = categoryLabel.replace(/\s/g, '_');
          const categoryHeaderId = `${formattedCategoryLabelId}-header`;
          const categorySectionId = `${formattedCategoryLabelId}-section`;
          const categoryTitle = categoryLabel.split(' ');

          return (
            <CarouselSection key={categoryLabel}>
              <CarouselHeadingContainer>
                <CarouselHeading>
                  {userPoints >= categoryPoints ? (
                    <HeadingLoyaltyIcon />
                  ) : (
                    <HeadingLockedIcon fill={Styles.color.contrastBackground} />
                  )}
                  <CarouselHeadingLabel>
                    {categoryTitle[0] + ' '}
                    {formatMessage({ id: 'cartLoyaltyPointsTitle' })}
                  </CarouselHeadingLabel>
                  {userPoints < categoryPoints && (
                    <PointsNeededText>
                      {formatMessage(
                        { id: 'yourXPointsAway' },
                        {
                          points: <span>{categoryPoints - userPoints}</span>,
                          pointsSlang: formatMessage({ id: 'crowns' }),
                        }
                      )}
                    </PointsNeededText>
                  )}
                </CarouselHeading>
              </CarouselHeadingContainer>
              {isMobile() ? (
                <StyledRewardsCarousel>
                  {category.rewards.map(carouselCardMapper)}
                </StyledRewardsCarousel>
              ) : (
                <CarouselSectionRewards
                  id={categorySectionId}
                  role="region"
                  aria-labelledby={categoryHeaderId}
                >
                  {category.rewards.map(carouselCardMapper)}
                </CarouselSectionRewards>
              )}
            </CarouselSection>
          );
        })}
      </LoyaltyIncentivesGrid>
    );
  }

  return (
    <LoyaltyIncentivesGrid
      selectedIncentive={selectedReward}
      refetch={refetchRewards}
      goBack={rewardsRoute}
      engineIncentivesMap={engineRewardsMap}
      setScrollContainerRef={setScrollContainerRef}
      offersInCooldown={false}
      isWidget={isWidget}
      setSelectedRewardIdFromWidget={setSelectedRewardIdFromWidget}
    >
      {title && <LoyaltyRewardsTitle>{title}</LoyaltyRewardsTitle>}
      {rewardCategories.map(category => {
        const categoryLabel = category.label?.en;
        if (!category.rewards?.length || !categoryLabel) {
          return null;
        }

        const isCategorySelected = selectedCategories.includes(categoryLabel);

        const categoryHeight = category.rewards.length * REWARD_CARD_HEIGHT;
        const onClickCategoryHeader = () => {
          if (scrollTimeoutId) {
            // This will most likely not happen, but handle the case where
            // more than 1 scroll is queued up
            clearTimeout(scrollTimeoutId);
            setScrollTimeoutId(null);
          }

          const lastSelectedCat = !isCategorySelected ? categoryLabel : '';
          setLastSelectedCategory(lastSelectedCat);

          return setSelectedCategories(selected => {
            const entries = new Set(selected);
            if (entries.has(categoryLabel)) {
              entries.delete(categoryLabel);
            } else {
              entries.add(categoryLabel);
            }
            return Array.from(entries);
          });
        };

        const isCurrentSelection = lastSelectedCategory === categoryLabel;

        const formattedCategoryLabelId = categoryLabel.replace(/\s/g, '_');
        const categoryHeaderId = `${formattedCategoryLabelId}-header`;
        const categorySectionId = `${formattedCategoryLabelId}-section`;
        return (
          <RewardCategoryAccordion
            open={isCategorySelected}
            isCurrentSelection={isCurrentSelection}
            categoryHeight={categoryHeight}
            key={categoryLabel}
            ref={e => {
              if (isCurrentSelection) {
                lastSelectedCategoryRef.current = e;
              }
            }}
          >
            <CategoryHeader>
              <AccordionTrigger
                id={categoryHeaderId}
                aria-controls={categorySectionId}
                aria-expanded={isCategorySelected}
                onClick={onClickCategoryHeader}
              >
                {category?.image?.locale && (
                  <CategoryImage
                    alt={category.imageDescription?.locale || ''}
                    image={category.image.locale}
                    objectFitContain
                  />
                )}
                <CategoryLabel>{categoryLabel}</CategoryLabel>
                <Icon icon="select" color="icon-default" width="24px" aria-hidden />
              </AccordionTrigger>
            </CategoryHeader>
            <DelayedUnmountRewardContainer
              id={categorySectionId}
              role="region"
              aria-labelledby={categoryHeaderId}
              isVisible={isCategorySelected}
              delayTime={CATEGORY_TRANSITION_TIME}
            >
              {category.rewards.map(reward => {
                const rewardId = reward?._id;
                const incentive = sanityRewardsMap[rewardId || ''];

                const loyaltyRewardId = incentive?.loyaltyEngineId || '';
                const engineReward = engineRewardsMap[loyaltyRewardId];
                const rewardEvaluationResults = rewardsEvaluationMap[loyaltyRewardId];
                return (
                  incentive && (
                    <IncentiveCard
                      key={rewardId}
                      incentiveData={parseIncentiveData(incentive)}
                      handleClick={handleClick}
                      locked={lockOnImageEnabled && !!engineReward?.locked}
                    >
                      <RewardCardContent engineReward={engineReward}></RewardCardContent>
                      <IncentiveEvaluationMessages
                        incentiveEvaluationResults={rewardEvaluationResults}
                        hideIcon
                      />
                    </IncentiveCard>
                  )
                );
              })}
            </DelayedUnmountRewardContainer>
          </RewardCategoryAccordion>
        );
      })}
    </LoyaltyIncentivesGrid>
  );
};
